<script lang="ts">
    import type {LayoutPart, LayoutPartType, LayoutSettings} from './types';
    import type {CssSize} from 'shared/ui-widgets/types';
    import type {SvelteComponentConstructor} from 'core/types';
    import {exists} from 'shared/utils/custom-utils';
    import KpColumn from 'shared/layouts/grid-system/KpColumn.svelte';
    import KpRow from 'shared/layouts/grid-system/KpRow.svelte';

    export let part: LayoutPart;
    export let layoutSettings: LayoutSettings;
    export let supportedComponents: Record<string, SvelteComponentConstructor>;

    const layoutPartTypesComponents: Record<string, SvelteComponentConstructor> = {
        row: KpRow,
        column: KpColumn
    };

    if (part.type === 'component' && !exists(findComponentByName(part.componentName))) {
        throw new Error(
            `<KpLayoutPart /> received unsupported or null componentName! (componentName=${part.componentName})`
        );
    }

    const spacingSetting = getPartSpacingSetting();
    const componentProps = part.props?.componentProps ?? {};
    const columnRatios = part.props?.columnRatios ?? [];
    const columnNumber = part.props?.columnNumber;
    const rowWidth = part.props?.rowWidth;
    const isAside = part.props?.isAside === true;

    function findComponentByName(name?: string): any {
        if (!exists(name)) {
            return null;
        }

        return supportedComponents[name];
    }

    function getPartSpacingSetting(): CssSize {
        if (part.type === 'component') {
            return '0px';
        }

        return part.type === 'column' ? layoutSettings.columnGap : layoutSettings.rowGap;
    }

    function getColumnOrRowLayoutPartProps(partType: LayoutPartType) {
        if (partType === 'row') {
            return {columnRatios, width: rowWidth, horizontalGap: spacingSetting};
        }

        return {column: columnNumber, aside: isAside, verticalGap: spacingSetting};
    }

    function getLayoutPartComponent(partType: LayoutPartType): SvelteComponentConstructor {
        return layoutPartTypesComponents[partType];
    }
</script>

{#if part.type === 'component'}
    <svelte:component this={findComponentByName(part.componentName)} {...componentProps}/>
{/if}

{#if part.type === 'column' || part.type === 'row'}
    <svelte:component this={getLayoutPartComponent(part.type)} {...getColumnOrRowLayoutPartProps(part.type)}>
        {#if exists(part.parts)}
            {#each part.parts as subPart}
                <svelte:self part={subPart}
                             {supportedComponents}
                             {layoutSettings}/>
            {/each}
        {/if}
    </svelte:component>
{/if}